import { ConfigFormHeader } from '@/components/ConfigFormHeader'
import { PrequalificationConfig } from '@/types/configuration'
import { Alert, Card, Checkbox, Divider, Group, Stack, Switch, Text, TextInput } from '@mantine/core'
import { useForm } from '@mantine/form'
import { IconAlertCircle, IconExternalLink } from '@tabler/icons-react'
import { useEffect } from 'react'

interface PrequalificationFormProps {
  data: PrequalificationConfig | null
  onSave: (data: PrequalificationConfig) => void
  isSaving: boolean
}

export function PrequalificationForm({ data, onSave, isSaving }: PrequalificationFormProps) {
  const form = useForm<PrequalificationConfig>({
    initialValues: {
      enabled: false,
      prequalificationLink: '',
      offerOnAppointmentBooked: true,
      offerOnAffordabilityQuestion: true,
    },
    validate: {
      prequalificationLink: (value, values) => {
        if (values.enabled && !value) {
          return 'Prequalification link is required when enabled'
        }
        if (value && !isValidUrl(value)) {
          return 'Please enter a valid URL'
        }
        return null
      },
    },
  })

  useEffect(() => {
    if (data) {
      form.setValues({
        enabled: data.enabled ?? false,
        prequalificationLink: data.prequalificationLink ?? '',
        offerOnAppointmentBooked: data.offerOnAppointmentBooked ?? true,
        offerOnAffordabilityQuestion: data.offerOnAffordabilityQuestion ?? true,
      })
    }
  }, [data])

  const isEnabled = form.values.enabled

  return (
    <form onSubmit={form.onSubmit(onSave)}>
      <Stack gap="md">
        <ConfigFormHeader onReset={() => form.reset()} isSaving={isSaving} />
        {/* Master Enable/Disable Toggle */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Stack gap="md">
            <Group justify="space-between">
              <div>
                <Text fw={600}>Enable Prequalification</Text>
                <Text size="sm" c="dimmed">
                  Allow AI to offer soft credit pull prequalification to customers
                </Text>
              </div>
              <Switch
                size="lg"
                onLabel="ON"
                offLabel="OFF"
                checked={isEnabled}
                onChange={event => form.setFieldValue('enabled', event.currentTarget.checked)}
              />
            </Group>

            {!isEnabled && (
              <Alert icon={<IconAlertCircle size={16} />} color="yellow" variant="light">
                When disabled, prequalification will not be offered to customers during conversations.
              </Alert>
            )}
          </Stack>
        </Card>

        {/* Prequalification Link */}
        <Card shadow="sm" padding="lg" radius="md" withBorder style={{ opacity: isEnabled ? 1 : 0.5 }}>
          <Stack gap="md">
            <div>
              <Text fw={600}>Prequalification Link</Text>
              <Text size="sm" c="dimmed">
                The secure link to your prequalification form (hosted by your credit provider)
              </Text>
            </div>
            <TextInput
              placeholder="https://your-credit-provider.com/prequal/dealer-id"
              disabled={!isEnabled}
              leftSection={<IconExternalLink size={16} />}
              {...form.getInputProps('prequalificationLink')}
            />
            <Text size="xs" c="dimmed">
              This link will be sent to customers who consent to prequalification
            </Text>
          </Stack>
        </Card>

        {/* Trigger Settings */}
        <Card shadow="sm" padding="lg" radius="md" withBorder style={{ opacity: isEnabled ? 1 : 0.5 }}>
          <Stack gap="md">
            <div>
              <Text fw={600}>Trigger Settings</Text>
              <Text size="sm" c="dimmed">
                Choose when the AI should offer prequalification to customers
              </Text>
            </div>

            <Divider />

            <Checkbox
              label="After Appointment Booked"
              description="Offer prequalification after a customer schedules an appointment"
              disabled={!isEnabled}
              checked={form.values.offerOnAppointmentBooked}
              onChange={event => form.setFieldValue('offerOnAppointmentBooked', event.currentTarget.checked)}
            />

            <Checkbox
              label="On Affordability Questions"
              description="Offer prequalification when a customer asks about payments, budget, or financing"
              disabled={!isEnabled}
              checked={form.values.offerOnAffordabilityQuestion}
              onChange={event => form.setFieldValue('offerOnAffordabilityQuestion', event.currentTarget.checked)}
            />

            <Text size="xs" c="dimmed" mt="xs">
              Note: Customers can always request prequalification directly, regardless of these settings
            </Text>
          </Stack>
        </Card>

        {/* Form Actions */}
      </Stack>
    </form>
  )
}

// Helper function to validate URL
function isValidUrl(string: string): boolean {
  try {
    new URL(string)
    return true
  } catch {
    return false
  }
}
